<template>
  <div class="dash-page">
    <addLeaveRequestModal v-if="showRequestLeaveModal" :employeeId="account.id"
      @close-add-leave-request-modal="showRequestLeaveModal = false" @refresh-leave-requests="onLeaveRequested()" />

    <!-- Header -->
    <div class="dash-header">
      <div>
        <h1 class="dash-greeting">{{ greeting }}, {{ $filters.capitalize(account.fname) }}</h1>
        <p v-if="employment.department || employment.position" class="dash-subtitle">
          <span v-if="employment.department">{{ employment.department }}</span>
          <span v-if="employment.department && employment.position" class="sep">•</span>
          <span v-if="employment.position">{{ employment.position }}</span>
        </p>
      </div>
    </div>

    <div v-if="stats?.offboarding" class="dash-alert">
      <i class="pi pi-exclamation-triangle"></i>
      <span>
        An offboarding process (<strong>{{ prettify(stats.offboarding.type) }}</strong>) is in progress for your account
        <template v-if="stats.offboarding.effectiveDate">, effective <strong>{{ fmtDateLong(stats.offboarding.effectiveDate) }}</strong></template>.
      </span>
    </div>

    <!-- Row 1: attendance + quick actions -->
    <div class="row">
      <div v-if="hasAttendance" class="col-lg-7">
        <attendanceCheckinCard :employeeId="account.id" @attendance-changed="loadStats()" />
      </div>
      <div :class="hasAttendance ? 'col-lg-5' : 'col-lg-12'">
        <sectionCard title="Quick actions" icon="pi-bolt" fill>
          <div class="quick-actions">
            <a v-if="hasLeave" class="quick-action" @click="showRequestLeaveModal = true">
              <span class="qa-label">Request leave</span>
            </a>
            <router-link v-if="hasPayroll" class="quick-action" :to="{ name: PROFILE_PAYROLL_ROUTE, params: { id: account.id } }">
              <span class="qa-label">My payslips</span>
            </router-link>
            <router-link v-if="hasAttendance" class="quick-action" :to="{ name: PROFILE_ATTENDANCE_ROUTE, params: { id: account.id } }">
              <span class="qa-label">My attendance</span>
            </router-link>
            <router-link v-if="hasLeave" class="quick-action" :to="{ name: PROFILE_LEAVE_ROUTE, params: { id: account.id } }">
              <span class="qa-label">My leave</span>
              <span v-if="leave.pendingCount" class="qa-count">{{ leave.pendingCount }}</span>
            </router-link>
            <router-link class="quick-action" :to="{ name: PROFILE_GENERAL_ROUTE, params: { id: account.id } }">
              <span class="qa-label">My profile</span>
            </router-link>
            <router-link class="quick-action" :to="{ name: HELP_CENTER_ROUTE }">
              <span class="qa-label">Help center</span>
            </router-link>
          </div>

          <div class="up-next">
            <div class="up-next-title"><i class="pi pi-flag"></i> Up next</div>
            <div v-if="isLoading && !stats">
              <div class="dash-skeleton" style="height: 14px; width: 90%; margin-bottom: 10px;"></div>
              <div class="dash-skeleton" style="height: 14px; width: 70%;"></div>
            </div>
            <ul v-else-if="upNextItems.length" class="dash-list up-next-list">
              <li v-for="item in upNextItems" :key="item.key" class="dash-list-item">
                <span :class="['kpi-icon', 'kpi-icon-sm', `tint-${item.tint}`]"><i :class="['pi', item.icon]"></i></span>
                <div class="dash-list-main">
                  <p class="dash-list-title">{{ item.title }}</p>
                  <p class="dash-list-meta">{{ item.meta }}</p>
                </div>
                <div class="dash-list-side">
                  <span :class="['dash-badge', item.badgeClass || 'badge-soft-gray']">{{ item.when }}</span>
                </div>
              </li>
            </ul>
            <p v-else class="up-next-empty">Nothing scheduled in the next few weeks.</p>
          </div>
        </sectionCard>
      </div>
    </div>

    <!-- Row 2: KPIs -->
    <div v-if="kpiCount" class="row">
      <div v-if="hasAttendance" :class="kpiCol">
        <statCard label="Attendance rate" :value="month.attendanceRate" unit="%" icon="pi-check-circle" tint="green"
          :sub="`${month.presentDays} of ${month.workingDaysToDate} working days so far`" :loading="isLoading && !stats"
          :to="{ name: PROFILE_ATTENDANCE_ROUTE, params: { id: account.id } }" />
      </div>
      <div v-if="hasAttendance" :class="kpiCol">
        <statCard label="On-time arrivals" :value="month.punctualityRate" unit="%" icon="pi-stopwatch" tint="amber"
          :sub="`${month.lateDays} late · avg check-in ${avgCheckInLabel}`" :loading="isLoading && !stats" />
      </div>
      <div v-if="hasLeave" :class="kpiCol">
        <statCard label="Leave available" :value="leave.totalAvailable" unit=" days" icon="pi-sun" tint="blue"
          :sub="`${leave.totalTaken} taken · ${leave.pendingCount} pending request${leave.pendingCount === 1 ? '' : 's'}`"
          :loading="isLoading && !stats" :to="{ name: PROFILE_LEAVE_ROUTE, params: { id: account.id } }" />
      </div>
      <div v-if="hasAttendance" :class="kpiCol">
        <statCard label="Hours this month" :value="month.totalHours" unit=" h" icon="pi-hourglass" tint="purple"
          :sub="hasPayroll ? `avg ${month.avgHoursPerDay} h/day · ${overtime.monthHours} h overtime` : `avg ${month.avgHoursPerDay} h/day`" :loading="isLoading && !stats"
          :to="hasPayroll ? { name: PROFILE_OVERTIME_ROUTE, params: { id: account.id } } : null" />
      </div>
    </div>

    <!-- Row 3: calendar + leave -->
    <div v-if="hasAttendance || hasLeave" class="row">
      <div v-if="hasAttendance" :class="hasLeave ? 'col-lg-7' : 'col-lg-12'">
        <sectionCard title="My attendance" :subtitle="stats?.period?.label" icon="pi-calendar" :loading="isLoading" fill>
          <template #tools>
            <div class="legend-row d-none d-md-flex">
              <span class="legend-item"><span class="legend-dot" style="background: #2E7918;"></span>Present</span>
              <span class="legend-item"><span class="legend-dot" style="background: #D97706;"></span>Late</span>
              <span class="legend-item"><span class="legend-dot" style="background: #DC2626;"></span>Absent</span>
              <span class="legend-item"><span class="legend-dot" style="background: #2563EB;"></span>Leave</span>
              <span class="legend-item"><span class="legend-dot" style="background: #7C3AED;"></span>Holiday</span>
            </div>
          </template>
          <div class="stat-strip mb-3">
            <div class="stat-cell cell-green"><span class="n">{{ month.presentDays }}</span><span class="l">Present</span></div>
            <div class="stat-cell cell-amber"><span class="n">{{ month.lateDays }}</span><span class="l">Late</span></div>
            <div class="stat-cell cell-red"><span class="n">{{ month.absentDays }}</span><span class="l">Absent</span></div>
            <div class="stat-cell cell-blue"><span class="n">{{ month.leaveDays }}</span><span class="l">On leave</span></div>
            <div class="stat-cell cell-gray"><span class="n">{{ Math.max(0, month.workingDays - month.workingDaysToDate) }}</span><span class="l">Remaining</span></div>
          </div>
          <div class="dashboard-attendance">
            <dashboardCalendarView :calendarOptions="calendarOptions" />
          </div>
        </sectionCard>
      </div>
      <div v-if="hasLeave" :class="hasAttendance ? 'col-lg-5' : 'col-lg-12'">
        <sectionCard title="Leave balances" icon="pi-sun" actionLabel="Manage"
          :actionTo="{ name: PROFILE_LEAVE_ROUTE, params: { id: account.id } }" :loading="isLoading">
          <div v-if="isLoading && !stats">
            <div class="dash-skeleton" style="height: 14px; width: 60%; margin-bottom: 10px;"></div>
            <div class="dash-skeleton" style="height: 8px; width: 100%; margin-bottom: 18px;"></div>
            <div class="dash-skeleton" style="height: 14px; width: 50%; margin-bottom: 10px;"></div>
            <div class="dash-skeleton" style="height: 8px; width: 100%;"></div>
          </div>
          <template v-else-if="visibleBalances.length">
            <div v-for="balance in visibleBalances" :key="balance.id" :class="['dash-progress-row', !balance.eligible ? 'ineligible' : '']">
              <div class="dash-progress-head">
                <span class="name">{{ balance.name }}</span>
                <span v-if="!balance.eligible" class="val">{{ balance.eligibilityMessage }}</span>
                <span v-else-if="balance.balanceTracked" class="val"><strong>{{ balance.available }}</strong> / {{ balance.entitlement }} days left</span>
                <span v-else class="val">{{ balance.capPerYearDays ? `up to ${balance.capPerYearDays} days / yr` : 'As needed' }} · {{ balance.totalTaken }} taken</span>
              </div>
              <div class="dash-progress">
                <div :class="['dash-progress-fill', balanceFillClass(balance)]" :style="{ width: `${balancePercent(balance)}%` }"></div>
              </div>
            </div>
          </template>
          <emptyState v-else icon="pi-sun" title="No leave balances yet" message="Leave balances appear once HR sets up your leave policies." />
        </sectionCard>

        <sectionCard title="Recent leave requests" icon="pi-list" noPadding
          :badge="leave.pendingCount ? `${leave.pendingCount} pending` : ''"
          :actionTo="{ name: PROFILE_LEAVE_ROUTE, params: { id: account.id } }" actionLabel="View all">
          <ul v-if="leave.requests.length" class="dash-list">
            <li v-for="request in leave.requests" :key="request.id" class="dash-list-item">
              <span :class="['kpi-icon', 'kpi-icon-sm', leaveTint(request.status)]"><i class="pi pi-calendar"></i></span>
              <div class="dash-list-main">
                <p class="dash-list-title">{{ request.leaveType || 'Leave' }}</p>
                <p class="dash-list-meta">{{ formatRange(request.startDate, request.endDate) }} · {{ request.days }} day{{ request.days === 1 ? '' : 's' }}</p>
              </div>
              <div class="dash-list-side">
                <span :class="['dash-badge', leaveBadgeClass(request.status)]">{{ prettify(request.status) }}</span>
              </div>
            </li>
          </ul>
          <emptyState v-else icon="pi-calendar-plus" title="No leave requests yet" message="Your leave history will show up here.">
            <button class="btn btn-custom-secondary btn-sm" @click="showRequestLeaveModal = true"><i class="pi pi-plus"></i> Request leave</button>
          </emptyState>
        </sectionCard>
      </div>
    </div>

    <!-- Row 4: recent attendance + payout -->
    <div v-if="hasAttendance || hasPayroll" class="row">
      <div v-if="hasAttendance" :class="hasPayroll ? 'col-lg-7' : 'col-lg-12'">
        <sectionCard title="Recent attendance" subtitle="Your last working days this month" icon="pi-history" noPadding fill
          :actionTo="{ name: PROFILE_ATTENDANCE_ROUTE, params: { id: account.id } }" actionLabel="Full history">
          <div v-if="recentAttendance.length" class="table-responsive">
            <table class="table dash-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Status</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th class="text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="day in recentAttendance" :key="day.date">
                  <td>
                    <div class="day-cell">
                      <span class="day-name">{{ day.dayLabel }}</span>
                      <span class="day-date">{{ fmtDate(day.date) }}</span>
                    </div>
                  </td>
                  <td>
                    <span :class="['status-pill', `status-${day.status}`]">{{ statusLabel(day) }}</span>
                  </td>
                  <td>
                    <span v-if="day.checkInTime">{{ $filters.getTimeOnly(day.checkInTime) }}</span>
                    <span v-else class="text-muted">—</span>
                    <small v-if="day.isLate" class="late-note">late</small>
                  </td>
                  <td>
                    <span v-if="day.checkOutTime">{{ $filters.getTimeOnly(day.checkOutTime) }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-right"><strong v-if="day.minutesWorked">{{ hoursLabel(day.minutesWorked) }}</strong><span v-else class="text-muted">—</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <emptyState v-else icon="pi-history" title="No attendance recorded yet" message="Your check-ins for this month will be listed here." />
        </sectionCard>
      </div>
      <div v-if="hasPayroll" :class="hasAttendance ? 'col-lg-5' : 'col-lg-12'">
        <sectionCard title="Last payout" :subtitle="payroll.latest ? payroll.latest.periodLabel : 'No approved payroll yet'" icon="pi-wallet" fill
          :actionTo="{ name: PROFILE_PAYROLL_ROUTE, params: { id: account.id } }" actionLabel="Payslips">
          <template #tools>
            <a class="dash-card-link" @click="isBlurred = !isBlurred">
              <i :class="['pi', isBlurred ? 'pi-eye' : 'pi-eye-slash']"></i> {{ isBlurred ? 'Show' : 'Hide' }}
            </a>
          </template>
          <template v-if="payroll.latest">
            <div :class="['payout-content', isBlurred ? 'blur-effect' : '']">
              <div class="payout-hero">
                <div>
                  <div class="payout-label">Net pay</div>
                  <div class="payout-value">{{ money(payroll.latest.netPay) }}</div>
                </div>
                <span :class="['dash-badge', payroll.latest.status === 'approved' ? 'badge-soft-green' : 'badge-soft-amber']">
                  <span class="dot"></span>{{ prettify(payroll.latest.status) }}
                </span>
              </div>
              <div class="payout-breakdown">
                <div class="payout-row"><span>Gross salary</span><strong>{{ money(payroll.latest.grossSalary) }}</strong></div>
                <div class="payout-row"><span>Basic salary</span><strong>{{ money(payroll.latest.basicSalary) }}</strong></div>
                <div v-if="payroll.latest.overtimePay" class="payout-row"><span>Overtime pay</span><strong class="text-success">+{{ money(payroll.latest.overtimePay) }}</strong></div>
                <div class="payout-row"><span>Income tax</span><strong class="text-danger">-{{ money(payroll.latest.incomeTax) }}</strong></div>
                <div class="payout-row"><span>Pension</span><strong class="text-danger">-{{ money(payroll.latest.pension) }}</strong></div>
                <div class="payout-row"><span>Total deductions</span><strong class="text-danger">-{{ money(payroll.latest.totalDeduction) }}</strong></div>
              </div>
              <div v-if="payroll.history.length > 1" class="payout-chart">
                <div class="payout-chart-title">Net pay · last {{ payroll.history.length }} periods</div>
                <apexchart type="bar" height="90" :options="payoutChartOptions" :series="payoutSeries" />
              </div>
            </div>
          </template>
          <emptyState v-else icon="pi-wallet" title="No payslips yet" message="Approved payroll for you will appear here." />
          <template v-if="payroll.latest" #footer>
            <span>{{ payroll.yearLabel }} to date: <strong :class="isBlurred ? 'blur-effect' : ''">{{ money(payroll.yearNet) }}</strong> net across {{ yearPayrollCount }} payout{{ yearPayrollCount === 1 ? '' : 's' }}</span>
          </template>
        </sectionCard>
      </div>
    </div>

    <!-- Row 5: holidays, projects, employment -->
    <div class="row">
      <div :class="row5Col">
        <sectionCard title="Upcoming holidays" subtitle="Next 60 days" icon="pi-star" noPadding fill>
          <ul v-if="holidays.length" class="dash-list">
            <li v-for="holiday in holidays" :key="holiday.id + holiday.date" class="dash-list-item">
              <span class="date-block">
                <span class="date-block-day">{{ dayOfMonth(holiday.date) }}</span>
                <span class="date-block-month">{{ monthShort(holiday.date) }}</span>
              </span>
              <div class="dash-list-main">
                <p class="dash-list-title">{{ holiday.title }}</p>
                <p class="dash-list-meta">{{ holiday.type === 'organization' ? 'Organization holiday' : 'Public holiday' }}</p>
              </div>
              <div class="dash-list-side">
                <span class="dash-badge badge-soft-purple">{{ whenLabel(holiday.daysUntil) }}</span>
              </div>
            </li>
          </ul>
          <emptyState v-else icon="pi-star" title="No upcoming holidays" message="No holidays in the next 60 days." />
        </sectionCard>
      </div>
      <div v-if="hasProjects" :class="row5Col">
        <sectionCard title="My projects" icon="pi-briefcase" noPadding fill :badge="projects.length ? `${projects.length} active` : ''">
          <ul v-if="projects.length" class="dash-list">
            <li v-for="project in projects" :key="project.id" class="dash-list-item">
              <span class="kpi-icon kpi-icon-sm tint-teal"><i class="pi pi-briefcase"></i></span>
              <div class="dash-list-main">
                <p class="dash-list-title">{{ project.name }}</p>
                <p class="dash-list-meta">
                  <template v-if="project.role">{{ $filters.capitalize(project.role) }}</template>
                  <template v-if="project.role && project.client"> · </template>
                  <template v-if="project.client">{{ project.client }}</template>
                  <template v-if="!project.role && !project.client">Team member</template>
                </p>
              </div>
              <div class="dash-list-side">
                <span :class="['dash-badge', projectBadgeClass(project.status)]">{{ prettify(project.status) }}</span>
                <span v-if="project.endDate" class="muted">Due {{ fmtDate(project.endDate) }}</span>
              </div>
            </li>
          </ul>
          <emptyState v-else icon="pi-briefcase" title="No active projects" message="Projects you are assigned to will appear here." />
        </sectionCard>
      </div>
      <div :class="row5Col">
        <sectionCard title="Employment snapshot" icon="pi-id-card" fill
          :actionTo="{ name: PROFILE_GENERAL_ROUTE, params: { id: account.id } }" actionLabel="Profile">
          <dl class="snapshot">
            <div class="snapshot-row"><dt>Department</dt><dd>{{ employment.department || '—' }}</dd></div>
            <div class="snapshot-row"><dt>Position</dt><dd>{{ employment.position || '—' }}</dd></div>
            <div class="snapshot-row"><dt>Contract</dt><dd>{{ employment.contractType ? prettify(employment.contractType) : '—' }}</dd></div>
            <div class="snapshot-row"><dt>Contract ends</dt><dd>{{ employment.contractEnd ? fmtDateLong(employment.contractEnd) : 'Open-ended' }}</dd></div>
            <div v-if="hasAttendance" class="snapshot-row"><dt>Shift</dt><dd>{{ employment.shiftName || 'Not assigned' }}</dd></div>
            <div class="snapshot-row"><dt>Hire date</dt><dd>{{ stats?.employee?.hireDate ? fmtDateLong(stats.employee.hireDate) : '—' }}</dd></div>
            <div class="snapshot-row"><dt>Tenure</dt><dd>{{ stats?.employee?.tenure?.label || '—' }}</dd></div>
          </dl>
        </sectionCard>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import dayGridPlugin from '@fullcalendar/daygrid';
import bootstrapPlugin from '@fullcalendar/bootstrap';
import attendanceCheckinCard from '@/components/dashboard/Attendance_Checkin_Card.vue';
import sectionCard from '@/components/dashboard/Dashboard_Section_Card.vue';
import statCard from '@/components/dashboard/Dashboard_Stat_Card.vue';
import emptyState from '@/components/dashboard/Dashboard_Empty_State.vue';
import dashboardCalendarView from '@/components/attendance/Dashboard_Calendar_View.vue';
import addLeaveRequestModal from '@/components/leave_request/Add_Leave_Request_Modal.vue';
import { GET_EMPLOYEE_DASHBOARD_STATS } from '@/store/action_types';
import {
  HELP_CENTER_ROUTE,
  PROFILE_ATTENDANCE_ROUTE,
  PROFILE_GENERAL_ROUTE,
  PROFILE_LEAVE_ROUTE,
  PROFILE_OVERTIME_ROUTE,
  PROFILE_PAYROLL_ROUTE,
} from '@/router/routes';
import { MONTHS_ABBREV } from '@/utils/const';
import { PlatformModule } from '@/utils/enums';

const EMPTY_MONTH = {
  workingDays: 0,
  workingDaysToDate: 0,
  presentDays: 0,
  lateDays: 0,
  absentDays: 0,
  leaveDays: 0,
  onTimeDays: 0,
  attendanceRate: 0,
  punctualityRate: 0,
  totalHours: 0,
  avgHoursPerDay: 0,
  avgCheckInTime: null,
};

export default {
  name: 'EmployeeDashboard',
  components: {
    apexchart: VueApexCharts,
    attendanceCheckinCard,
    sectionCard,
    statCard,
    emptyState,
    dashboardCalendarView,
    addLeaveRequestModal,
  },
  data() {
    return {
      HELP_CENTER_ROUTE,
      PROFILE_ATTENDANCE_ROUTE,
      PROFILE_GENERAL_ROUTE,
      PROFILE_LEAVE_ROUTE,
      PROFILE_OVERTIME_ROUTE,
      PROFILE_PAYROLL_ROUTE,
      now: new Date(),
      isBlurred: true,
      showRequestLeaveModal: false,
      calendarOptions: {
        plugins: [dayGridPlugin, bootstrapPlugin],
        initialView: 'dayGridMonth',
        themeSystem: 'bootstrap',
        headerToolbar: false,
        height: 'auto',
        fixedWeekCount: false,
        dayMaxEvents: 2,
        displayEventTime: false,
        eventDisplay: 'block',
        events: [],
      },
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    modules() {
      return this.$store.getters.enabledModules;
    },
    hasAttendance() {
      return this.modules.includes(PlatformModule.attendance);
    },
    hasLeave() {
      return this.modules.includes(PlatformModule.leave);
    },
    hasPayroll() {
      return this.modules.includes(PlatformModule.payroll);
    },
    hasProjects() {
      return this.modules.includes(PlatformModule.projectManagement);
    },
    /** KPI tiles shown for the licensed modules; they share the row evenly. */
    kpiCount() {
      return (this.hasAttendance ? 3 : 0) + (this.hasLeave ? 1 : 0);
    },
    kpiCol() {
      if (this.kpiCount >= 4) return 'col-xl-3 col-md-6';
      if (this.kpiCount === 3) return 'col-xl-4 col-md-6';
      if (this.kpiCount === 2) return 'col-xl-6 col-md-6';
      return 'col-xl-12';
    },
    row5Col() {
      return this.hasProjects ? 'col-lg-4' : 'col-lg-6';
    },
    stats() {
      return this.$store.getters.employeeDashboardStats.data;
    },
    isLoading() {
      return this.$store.getters.employeeDashboardStats.isLoading;
    },
    greeting() {
      const hour = this.now.getHours();
      if (hour < 12) return 'Good morning';
      if (hour < 17) return 'Good afternoon';
      return 'Good evening';
    },
    employment() {
      return this.stats?.employment ?? {};
    },
    month() {
      return this.stats?.attendance?.month ?? EMPTY_MONTH;
    },
    leave() {
      return this.stats?.leave ?? { balances: [], totalAvailable: 0, totalTaken: 0, pendingCount: 0, requests: [], upcoming: null };
    },
    payroll() {
      return this.stats?.payroll ?? { latest: null, history: [], yearNet: 0, yearGross: 0, yearLabel: null, count: 0 };
    },
    overtime() {
      return this.stats?.overtime ?? { monthHours: 0, monthEntries: 0, recent: [] };
    },
    holidays() {
      return this.stats?.holidays?.upcoming ?? [];
    },
    projects() {
      return this.stats?.projects ?? [];
    },
    recentAttendance() {
      return this.stats?.attendance?.recent ?? [];
    },
    visibleBalances() {
      return [...this.leave.balances].sort((a, b) => Number(b.eligible) - Number(a.eligible));
    },
    avgCheckInLabel() {
      const time = this.month.avgCheckInTime;
      if (!time) return '—';
      return this.$filters.formatTimeToHHMM(this.$filters.toLocalHHMMSS(time));
    },
    yearPayrollCount() {
      const year = this.payroll.yearLabel;
      if (!year) return 0;
      return this.payroll.history.filter((p) => p.year === year).length || this.payroll.count;
    },
    upNextItems() {
      const items = [];
      const todayAtt = this.stats?.attendance?.today;

      if (this.hasLeave && this.leave.upcoming) {
        const days = this.daysUntil(this.leave.upcoming.startDate);
        items.push({
          key: 'leave',
          icon: 'pi-sun',
          tint: 'blue',
          title: `${this.leave.upcoming.leaveType || 'Leave'} · ${this.leave.upcoming.days} day${this.leave.upcoming.days === 1 ? '' : 's'}`,
          meta: this.formatRange(this.leave.upcoming.startDate, this.leave.upcoming.endDate),
          when: this.whenLabel(days),
          badgeClass: 'badge-soft-blue',
          sort: days,
        });
      }

      this.holidays.slice(0, 2).forEach((holiday) => {
        items.push({
          key: `holiday-${holiday.id}-${holiday.date}`,
          icon: 'pi-star',
          tint: 'purple',
          title: holiday.title,
          meta: this.fmtDateLong(holiday.date),
          when: this.whenLabel(holiday.daysUntil),
          badgeClass: 'badge-soft-purple',
          sort: holiday.daysUntil,
        });
      });

      if (this.employment.contractEnd) {
        const days = this.daysUntil(this.employment.contractEnd);
        if (days >= 0 && days <= 60) {
          items.push({
            key: 'contract',
            icon: 'pi-file',
            tint: 'amber',
            title: 'Contract end date',
            meta: this.fmtDateLong(this.employment.contractEnd),
            when: this.whenLabel(days),
            badgeClass: 'badge-soft-amber',
            sort: days,
          });
        }
      }

      if (this.hasAttendance && todayAtt && !todayAtt.isScheduled && todayAtt.nextWorkingDay) {
        const days = this.daysUntil(todayAtt.nextWorkingDay.date);
        items.push({
          key: 'next-day',
          icon: 'pi-clock',
          tint: 'green',
          title: `Next working day · ${todayAtt.nextWorkingDay.dayName}`,
          meta: `Shift starts ${this.$filters.formatTimeToHHMM(this.$filters.toLocalHHMMSS(todayAtt.nextWorkingDay.clockInTime))}`,
          when: this.whenLabel(days),
          badgeClass: 'badge-soft-green',
          sort: days,
        });
      }

      return items.sort((a, b) => a.sort - b.sort).slice(0, 4);
    },
    payoutChartOptions() {
      return {
        chart: { type: 'bar', sparkline: { enabled: true }, toolbar: { show: false }, animations: { enabled: true } },
        colors: ['#2E7918'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        xaxis: { categories: this.payroll.history.map((p) => p.periodLabel) },
        tooltip: {
          x: { show: true },
          y: { formatter: (value) => this.money(value), title: { formatter: () => 'Net pay' } },
        },
      };
    },
    payoutSeries() {
      return [{ name: 'Net pay', data: this.payroll.history.map((p) => Math.round(p.netPay)) }];
    },
  },
  methods: {
    asLocalDate(value) {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00`;
      return value;
    },
    fmtDate(value) {
      return this.$filters.getDateOnlyInNumberFormat(this.asLocalDate(value));
    },
    fmtDateLong(value) {
      return this.$filters.getDateOnly(this.asLocalDate(value));
    },
    loadStats() {
      return this.$store
        .dispatch(GET_EMPLOYEE_DASHBOARD_STATS, { employeeId: this.account.id, organizationId: this.account.organizationId })
        .then((res) => {
          if (res) this.applyCalendarEvents();
        });
    },
    applyCalendarEvents() {
      const events = (this.stats?.attendance?.calendar ?? []).map((event) => ({
        ...event,
        backgroundColor: event.color,
        borderColor: event.color,
        textColor: '#ffffff',
      }));
      this.calendarOptions = { ...this.calendarOptions, events };
    },
    onLeaveRequested() {
      this.showRequestLeaveModal = false;
      this.loadStats();
    },
    money(value) {
      return this.$filters.formatPrice(value ?? 0, 'ETB');
    },
    prettify(value) {
      if (!value) return '';
      return String(value)
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    hoursLabel(minutes) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
    },
    daysUntil(dateValue) {
      if (!dateValue) return null;
      const target = new Date(dateValue);
      const targetUTC = Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
      const now = new Date();
      const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      return Math.round((targetUTC - todayUTC) / 86400000);
    },
    whenLabel(days) {
      if (days === null || days === undefined) return '';
      if (days <= 0) return 'Today';
      if (days === 1) return 'Tomorrow';
      return `in ${days} days`;
    },
    formatRange(start, end) {
      const s = this.fmtDate(start);
      const e = this.fmtDate(end);
      return s === e ? s : `${s} → ${e}`;
    },
    dayOfMonth(dateStr) {
      return new Date(dateStr).getUTCDate();
    },
    monthShort(dateStr) {
      return MONTHS_ABBREV[new Date(dateStr).getUTCMonth()];
    },
    statusLabel(day) {
      const labels = { present: 'Present', late: 'Late', absent: 'Absent', leave: 'On leave', holiday: 'Holiday', off: 'Off', pending: 'Pending', upcoming: 'Upcoming' };
      if (day.status === 'holiday' && day.holidayName) return day.holidayName;
      return labels[day.status] ?? this.prettify(day.status);
    },
    leaveBadgeClass(status) {
      return { pending: 'badge-soft-amber', approved: 'badge-soft-green', rejected: 'badge-soft-red', cancelled: 'badge-soft-gray' }[status] ?? 'badge-soft-gray';
    },
    leaveTint(status) {
      return { pending: 'tint-amber', approved: 'tint-green', rejected: 'tint-red', cancelled: 'tint-gray' }[status] ?? 'tint-gray';
    },
    projectBadgeClass(status) {
      return { active: 'badge-soft-green', 'in-progress': 'badge-soft-blue', planning: 'badge-soft-purple', 'on-hold': 'badge-soft-amber' }[status] ?? 'badge-soft-gray';
    },
    balancePercent(balance) {
      if (!balance.eligible) return 0;
      if (!balance.balanceTracked) {
        if (balance.capPerYearDays) return Math.min(100, (balance.totalTaken / balance.capPerYearDays) * 100);
        return 100;
      }
      if (!balance.entitlement) return 0;
      return Math.max(0, Math.min(100, (balance.available / balance.entitlement) * 100));
    },
    balanceFillClass(balance) {
      if (!balance.eligible) return 'fill-gray';
      if (!balance.balanceTracked) return 'fill-blue';
      const percent = this.balancePercent(balance);
      if (percent > 50) return '';
      if (percent > 20) return 'fill-amber';
      return 'fill-red';
    },
  },
  mounted() {
    this.loadStats();
  },
};
</script>

<style scoped>
.dash-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #78350f;
  font-size: 0.82rem;
  margin-bottom: 1.25rem;
}

.dash-alert .pi {
  color: #d97706;
}

.kpi-icon-sm {
  width: 34px;
  height: 34px;
  border-radius: 9px;
}

.kpi-icon-sm .pi {
  font-size: 0.85rem;
}

.up-next {
  margin-top: 14px;
  border-top: 1px solid #F0F0F2;
  padding-top: 10px;
}

.up-next-title {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 4px;
}

.up-next-title .pi {
  font-size: 0.7rem;
  margin-right: 4px;
  color: #2E7918;
}

.up-next-list .dash-list-item {
  padding-left: 0;
  padding-right: 0;
}

.up-next-empty {
  font-size: 0.78rem;
  color: #9ca3af;
  margin: 6px 0 0 0;
}

.day-cell {
  display: flex;
  flex-direction: column;
}

.day-cell .day-name {
  font-weight: 600;
  color: #18181b;
}

.day-cell .day-date {
  font-size: 0.7rem;
  color: #9ca3af;
}

.late-note {
  margin-left: 6px;
  font-size: 0.65rem;
  color: #b45309;
  font-weight: 600;
  text-transform: uppercase;
}

.blur-effect {
  filter: blur(6px);
  transition: filter 0.3s ease-in-out;
  user-select: none;
}

.payout-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.payout-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.payout-value {
  font-size: 1.7rem;
  font-weight: 700;
  color: #2E7918;
  letter-spacing: -0.02rem;
  line-height: 1.2;
}

.payout-breakdown {
  border-top: 1px solid #F0F0F2;
}

.payout-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 7px 0;
  border-bottom: 1px solid #F3F4F6;
  color: #6b7280;
}

.payout-row strong {
  color: #18181b;
  font-weight: 600;
}

.payout-row strong.text-danger {
  color: #dc2626 !important;
}

.payout-row strong.text-success {
  color: #2E7918 !important;
}

.payout-chart {
  margin-top: 12px;
}

.payout-chart-title {
  font-size: 0.72rem;
  color: #6b7280;
  margin-bottom: 4px;
}

.date-block {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #f5f3ff;
  color: #7c3aed;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
}

.date-block-day {
  font-size: 1rem;
  font-weight: 700;
}

.date-block-month {
  font-size: 0.62rem;
  text-transform: uppercase;
  font-weight: 600;
  margin-top: 2px;
}

.snapshot {
  margin: 0;
}

.snapshot-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #F3F4F6;
  font-size: 0.8rem;
}

.snapshot-row:last-child {
  border-bottom: none;
}

.snapshot-row dt {
  color: #6b7280;
  font-weight: 500;
  margin: 0;
  flex-shrink: 0;
}

.snapshot-row dd {
  color: #18181b;
  font-weight: 600;
  margin: 0;
  text-align: right;
}

.dash-progress-row.ineligible .name {
  color: #9ca3af;
}

.btn-sm {
  font-size: 0.78rem;
  padding: 5px 12px;
}
</style>
